'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';

type OrbitLike = {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
};

/**
 * Idle supersampling (adaptive SSAA).
 *
 * Product hero shots are static most of the time, and the dominant aliasing in
 * this scene is NOT geometric — it's alpha-cutout engravings, bump/normal relief
 * and specular sparkle on metal. None of those are touched by MSAA; only rendering
 * at a higher internal resolution and downsampling (supersampling) fixes them.
 *
 * Strategy: render at a modest DPR while the user orbits (cheap, responsive), then
 * bump the pixel ratio up once the camera has been still briefly. The higher-res
 * frame is drawn once and held, so the cost is paid only when it improves the
 * picture the user is looking at.
 *
 * CRITICAL CEILING: this technique inflates the canvas *backing store* and lets the
 * browser downscale it to the CSS size. The browser uses a bilinear filter, which is
 * a correct box average ONLY at an exact 2:1 ratio (samples the full 2×2 block). At
 * 3:1+ it undersamples (2×2 out of 3×3) → the result gets SOFTER and slightly worse,
 * not better. So the useful maximum backing ratio is 2× the physical device pixels.
 * Going higher requires an in-engine downsample pass with a proper filter.
 *
 * The pixel ratio is reported back via `onDpr` so the parent can feed it into the
 * Canvas `dpr` prop. That keeps a single source of truth — R3F re-applies the
 * `dpr` prop on every commit, so driving `setDpr` directly gets snapped back.
 *
 * Relies on OrbitControls `makeDefault` so `state.controls` exposes start/end/change.
 */
export function IdleSupersampler({
  interactDpr,
  desiredIdleDpr = 3,
  idleDelayMs = 260,
  /** Cap total supersampled pixels so high-DPR + MSAA byte buffers stay allocatable. */
  pixelBudget = 20_000_000,
  onDpr,
  onIdle,
}: {
  interactDpr: number;
  desiredIdleDpr?: number;
  idleDelayMs?: number;
  pixelBudget?: number;
  onDpr: (dpr: number) => void;
  /** Fired true when the camera has been still past the delay, false on any movement. */
  onIdle?: (idle: boolean) => void;
}) {
  const controls = useThree((s) => s.controls) as unknown as OrbitLike | null;
  const invalidate = useThree((s) => s.invalidate);
  const size = useThree((s) => s.size);

  const idleDpr = useMemo(() => {
    const cssPx = Math.max(1, size.width * size.height);
    const maxByBudget = Math.sqrt(pixelBudget / cssPx);
    // Browser bilinear downscale is only a clean box average at exactly 2:1, so never
    // render above 2× the physical device pixels — beyond that it gets softer, not better.
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const downscaleCeil = 2 * dpr;
    return Math.max(interactDpr, Math.min(desiredIdleDpr, maxByBudget, downscaleCeil));
  }, [size.width, size.height, interactDpr, desiredIdleDpr, pixelBudget]);

  const currentRef = useRef(interactDpr);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const applyDpr = (v: number) => {
      if (Math.abs(currentRef.current - v) < 0.01) return;
      currentRef.current = v;
      onDpr(v);
      // Draw the newly-sized frame under the demand loop (after the commit resizes).
      requestAnimationFrame(() => invalidate());
    };

    const goInteract = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      applyDpr(interactDpr);
      onIdle?.(false);
    };

    const scheduleIdle = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        applyDpr(idleDpr);
        onIdle?.(true);
      }, idleDelayMs);
    };

    // Any camera movement → drop to the responsive DPR, then re-arm the idle timer.
    const onStart = () => goInteract();
    const onChange = () => {
      goInteract();
      scheduleIdle();
    };
    const onEnd = () => scheduleIdle();

    if (controls) {
      controls.addEventListener('start', onStart);
      controls.addEventListener('change', onChange);
      controls.addEventListener('end', onEnd);
    }

    // Scene usually loads static → supersample after the initial settle.
    scheduleIdle();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (controls) {
        controls.removeEventListener('start', onStart);
        controls.removeEventListener('change', onChange);
        controls.removeEventListener('end', onEnd);
      }
    };
  }, [controls, invalidate, onDpr, onIdle, interactDpr, idleDpr, idleDelayMs]);

  return null;
}
